/**
 * What the skier was doing across a recorded day: stopped, on a lift, on a run,
 * or moving with no detected feature. Stops come from the speed of each point.
 * Lifts and runs come from its feature id, looked up in the ski area the point
 * was recorded in.
 */

import { TRACK_GAP_SECONDS } from './recordedDayTrack';
import { toTrackSpan } from './dayConnectivity';

/** Below this a point counts as stopped, since GPS reports a little speed at rest. */
const IDLE_SPEED_MPS = 0.5;

/** A stop shorter than this is part of the skiing around it, like a lift line or a pause on a run. */
const MIN_IDLE_MS = 5 * 60 * 1000;

/** Movement this short and this slow between two stops is GPS noise, so the stop carries on through it. */
const MAX_NOISE_MS = 30 * 1000;
const MAX_NOISE_SPEED_MPS = 1.5;

/** A lift or run shorter than this is detection switching between neighbors, so the one before it carries on. */
const MIN_FEATURE_MS = 20 * 1000;

const durationOf = ({ startMs, endMs }) => endMs - startMs;

const mergeTouching = (stretches, isSame) => stretches.reduce((merged, stretch) => {
  const last = merged[merged.length - 1];
  if (last && isSame(last, stretch) && last.endMs === stretch.startMs) {
    last.endMs = stretch.endMs;
    last.peakSpeed = Math.max(last.peakSpeed, stretch.peakSpeed);
  } else {
    merged.push({ ...stretch });
  }
  return merged;
}, []);

const sameStopState = (a, b) => a.idle === b.idle;

const isNoiseBetweenStops = (stretch, previous, next) => (
  !stretch.idle
  && durationOf(stretch) < MAX_NOISE_MS
  && stretch.peakSpeed < MAX_NOISE_SPEED_MPS
  && previous?.idle && previous.endMs === stretch.startMs
  && next?.idle && stretch.endMs === next.startMs
);

/**
 * Each piece with the feature it was on. Detection comes and goes on a run, so
 * a run carries on until the next detection. Detection holds for a whole lift
 * ride, so when it ends the ride is over, and carrying it on would count the
 * wait at the top as riding.
 */
const withCarriedFeature = (pieces, features) => {
  let carried = null;
  return pieces.map((piece) => {
    const detected = features[piece.skiAreaId]?.[piece.featureId] ?? null;
    carried = detected ?? (carried?.kind === 'run' ? carried : null);
    return { ...piece, feature: carried };
  });
};

/** A moving stretch cut wherever the detected lift or run changes. */
const splitByFeature = (stretch, pieces, features) => {
  const inside = pieces.filter(({ startMs, endMs }) => startMs >= stretch.startMs && endMs <= stretch.endMs);
  const byFeature = mergeTouching(withCarriedFeature(inside, features), (a, b) => a.feature === b.feature);

  return mergeTouching(byFeature, (a, b) => a.feature === b.feature || (a.feature !== null && durationOf(b) < MIN_FEATURE_MS))
    .map(({ feature, startMs, endMs }) => ({ kind: feature?.kind ?? 'moving', feature, startMs, endMs }));
};

/**
 * The day as stretches in order. A stop wins over a lift or run, because a
 * stopped point can still carry the feature detected before it. A point with
 * no valid speed (missing, or negative when the phone had no fix) is skipped,
 * and a recording gap is left out, so neither is reported as idle.
 *
 * @param {Array<Object>} locations - Time-sorted track; `timestamp` in seconds, `speed` in m/s
 * @param {Object} [features] - features[skiAreaId][featureId], from the recorded day response
 * @returns {Array<{ kind: 'idle'|'lift'|'run'|'moving', feature: Object|null, startMs: number, endMs: number }>}
 */
export const deriveDayActivity = (locations, features = {}) => {
  const known = locations.filter(({ speed }) => speed !== null && speed !== void 0 && speed >= 0);

  const pieces = [];
  known.slice(1).forEach((location, index) => {
    const previous = known[index];
    if (location.timestamp - previous.timestamp <= TRACK_GAP_SECONDS) {
      pieces.push({
        idle: previous.speed < IDLE_SPEED_MPS,
        peakSpeed: previous.speed,
        skiAreaId: previous.skiAreaId,
        featureId: previous.featureId,
        startMs: previous.timestamp * 1000,
        endMs: location.timestamp * 1000
      });
    }
  });

  const stretches = mergeTouching(pieces, sameStopState);
  const withoutNoise = mergeTouching(stretches.map((stretch, index) => (
    isNoiseBetweenStops(stretch, stretches[index - 1], stretches[index + 1]) ? { ...stretch, idle: true } : stretch
  )), sameStopState);
  const stops = mergeTouching(withoutNoise.map((stretch) => (
    stretch.idle && durationOf(stretch) < MIN_IDLE_MS ? { ...stretch, idle: false } : stretch
  )), sameStopState);

  return stops.flatMap((stretch) => (
    stretch.idle
      ? [{ kind: 'idle', feature: null, startMs: stretch.startMs, endMs: stretch.endMs }]
      : splitByFeature(stretch, pieces, features)
  ));
};

/** Each stretch placed on the track, as left and width in %. */
export const buildActivityStrip = (stretches, trackStartMs, trackEndMs) => stretches
  .map((stretch) => {
    const span = toTrackSpan(stretch.startMs, stretch.endMs, trackStartMs, trackEndMs);
    return span && { ...span, kind: stretch.kind, feature: stretch.feature };
  })
  .filter(Boolean);

/** The stretch at a moment of the day, or null inside a recording gap. */
export const activityAt = (stretches, ms) => stretches.find(({ startMs, endMs }) => startMs <= ms && ms <= endMs) ?? null;
